import type {ReactElement} from 'react';
import {useRef} from 'react';
import {Modal} from '@Components/modal';
import {MODALS_IDS} from '@Components/modal-container';
import {closeModal, openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {Panel, PanelContent, PanelFooter, PanelFooterPrimaryDefaultAction, PanelHeader} from '@Components/panel';
import {Size} from '@Components/button';
import {LayersPanel} from '@Components/poster-editor/components/layers-panel/layers-panel';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {RootState} from '@/store';
import styles from './poster-editor-layers-modal.module.scss';

const PANEL_ID = 'poster-editor-layers-panel';

export default function PosterEditorLayersModal(): ReactElement {
  const dispatch = useAppDispatch();
  const selectedCount = useAppSelector((state: RootState): number => {
    const currentPageId = state.posterEditor.currentPageId;
    return (state.posterEditor.activeItemIds[currentPageId] ?? []).length;
  });
  const isMobileWidth = useIsSmallerThanBreakpointWidth(ScreenBreakpoints.XS);
  const bodyRef = useRef<HTMLDivElement>(null);
  const {bottomShadow} = useTopBottomScrollShadow(bodyRef);

  const onClose = (): void => {
    dispatch(closeModal(MODALS_IDS.POSTER_EDITOR_LAYERS_MODAL));
  };

  const buttonText = selectedCount > 1 ? `${window.i18next.t('pmwjs_select')} (${selectedCount})` : window.i18next.t('pmwjs_select');

  return (
    <Modal
      modalId={MODALS_IDS.POSTER_EDITOR_LAYERS_MODAL}
      panelId={PANEL_ID}
      mobileHeight="500px"
      isBottomSheetOnMobile
      isBottomSheetNonBlocking
      modalHeight="500px"
      modalMaxWidth="500px"
      modalWidth="100%"
    >
      <Panel
        panelHeader={<PanelHeader title={window.i18next.t('pmwjs_select_layers')} onClose={onClose} isOpenedInModal />}
        panelContent={
          <PanelContent>
            <LayersPanel hideHeader onClose={onClose} bodyRef={bodyRef} />
          </PanelContent>
        }
        panelFooter={
          <PanelFooter
            fullWidthPrimaryActions={isMobileWidth}
            actionsContainerClassName="spacing-p-3"
            border={false}
            className={`${styles.footer} ${bottomShadow ? styles.footerScrolled : ''}`}
            primaryActions={[<PanelFooterPrimaryDefaultAction key="layers-select" size={Size.SMALL} className={styles.selectButton} onClick={onClose} text={buttonText} />]}
          />
        }
      />
    </Modal>
  );
}

export const openLayersModal = (): void => {
  window.PMW.redux.store.dispatch(
    openPanelInModal({
      modalId: MODALS_IDS.POSTER_EDITOR_LAYERS_MODAL,
      panelProps: {
        panelId: PANEL_ID,
      },
    })
  );
};
